import { BaseService } from '../base/BaseService';
import { ServiceError } from '../base/ServiceError';
import type { QueryOptions, ServiceResponse } from '../base/ServiceTypes';

import type { Database, Json } from '@/integrations/supabase/types';

export interface BrandAspirationsData {
  inspiration_brands: string[];
  aesthetic: string;
  voice_casual: number;
  voice_boldness: number;
}

type BrandVoiceRow = Database['public']['Tables']['user_brand_voices']['Row'];
type BrandVoiceInsert = Database['public']['Tables']['user_brand_voices']['Insert'];
type BrandVoiceUpdate = Database['public']['Tables']['user_brand_voices']['Update'];

export type BrandVoiceWithDefaults = BrandVoiceRow;

/**
 * The prompt-critical voice fields the Settings → Brand Voice editor writes.
 * Every field is optional so the editor can send a partial diff; text fields
 * clear to `null` when emptied, array fields to `[]`. `brand_kit_id` is here
 * only so a legacy null-scoped voice can be stamped onto the resolved kit on
 * its first save (contract rule 7 — null-scoped voices are transitional).
 */
export interface VoiceFieldsPatch {
  tone?: string;
  industry?: string;
  target_audience?: string;
  business_overview?: string;
  mission_statement?: string;
  unique_selling_proposition?: string;
  content_pillars?: string[];
  keywords?: string[];
  approved_phrases?: string[];
  avoid_phrases?: string[];
  brand_kit_id?: string;
}

export class BrandVoiceService extends BaseService {
  private static instance: BrandVoiceService;

  private constructor() {
    super({
      defaultTTL: 5 * 60 * 1000, // 5 minutes
      enableCache: true,
      enableRetry: true,
    });
  }

  public static getInstance(): BrandVoiceService {
    if (!BrandVoiceService.instance) {
      BrandVoiceService.instance = new BrandVoiceService();
    }
    return BrandVoiceService.instance;
  }

  /**
   * Get all brand voices for the current user
   */
  async getBrandVoices(options?: QueryOptions): Promise<ServiceResponse<BrandVoiceWithDefaults[]>> {
    return this.execute(async () => {
      const ownerId = await this.ensureAuthenticatedOwner();
      const cacheKey = `brand-voices-${ownerId}`;

      return this.executeWithCache(
        cacheKey,
        async () => {
          const query = this.buildQuery(
            this.client.from('user_brand_voices').select('*').eq('user_id', ownerId),
            options,
          );

          const { data, error } = await query;

          if (error) throw error;
          if (!data) return [];

          // Mark the first voice as default if no default is set
          const voices = data as BrandVoiceRow[];
          if (voices.length > 0 && !voices.some((v) => v.is_default)) {
            voices[0].is_default = true;
          }

          return voices;
        },
        options,
      );
    });
  }

  /**
   * Get a single brand voice by ID
   */
  async getBrandVoice(id: string): Promise<ServiceResponse<BrandVoiceRow | null>> {
    return this.execute(async () => {
      const ownerId = await this.ensureAuthenticatedOwner();
      const cacheKey = `brand-voice-${id}`;

      return this.executeWithCache(cacheKey, async () => {
        const { data, error } = await this.client
          .from('user_brand_voices')
          .select('*')
          .eq('id', id)
          .eq('user_id', ownerId)
          .maybeSingle();

        if (error) {
          throw error;
        }

        if (!data) {
          throw ServiceError.notFound('Brand voice');
        }

        return data;
      });
    });
  }

  /**
   * Validate and sanitize brand voice input
   */
  private validateAndSanitizeBrandVoice(
    voice: Omit<BrandVoiceInsert, 'user_id'>,
  ): Omit<BrandVoiceInsert, 'user_id'> {
    // Validate profile name
    if (!voice.profile_name || !voice.profile_name.trim()) {
      throw ServiceError.validationError('Profile name is required');
    }

    // Validate profile name format (alphanumeric, spaces, hyphens, underscores only)
    if (!voice.profile_name.match(/^[\w\s-]{1,50}$/)) {
      throw ServiceError.validationError(
        'Profile name must be 1-50 characters and contain only letters, numbers, spaces, hyphens, and underscores',
      );
    }

    // Sanitize text fields
    const sanitizeText = (text: string | undefined | null): string | undefined => {
      if (!text) return undefined;
      // Remove any HTML tags and trim
      return text.replace(/<[^>]*>/g, '').trim();
    };

    // Sanitize array fields
    const sanitizeArray = (arr: string[] | undefined | null): string[] | undefined => {
      if (!arr || !Array.isArray(arr)) return undefined;
      return arr
        .filter((item) => typeof item === 'string' && item.trim())
        .map((item) => sanitizeText(item)!)
        .filter(Boolean);
    };

    return {
      ...voice,
      profile_name: sanitizeText(voice.profile_name)!,
      bio: sanitizeText(voice.bio),
      target_audience: sanitizeText(voice.target_audience),
      content_pillars: sanitizeArray(voice.content_pillars),
      personality_traits: sanitizeArray(voice.personality_traits),
      core_values: sanitizeArray(voice.core_values),
      communication_style: sanitizeText(voice.communication_style),
    };
  }

  /**
   * Create a new brand voice
   */
  async createBrandVoice(
    voice: Omit<BrandVoiceInsert, 'user_id'>,
  ): Promise<ServiceResponse<BrandVoiceRow>> {
    return this.execute(async () => {
      const ownerId = await this.ensureAuthenticatedOwner();

      // Validate and sanitize input
      const sanitizedVoice = this.validateAndSanitizeBrandVoice(voice);

      const { data, error } = await this.client
        .from('user_brand_voices')
        .insert({
          ...sanitizedVoice,
          user_id: ownerId,
        } as BrandVoiceInsert)
        .select()
        .maybeSingle();

      if (error) throw error;
      if (!data) throw new Error('Failed to create brand voice');

      // Clear cache for user's brand voices
      this.clearCache(`brand-voices-${ownerId}`);

      return data;
    }, 'Failed to create brand voice');
  }

  /**
   * Onboarding-only idempotent write. `/onboarding` is a routed, repeatable
   * destination (Shopify reinstall lands ghost users back on it, back
   * button, `/style-guide/onboarding` dev launcher) — finishing the wizard
   * twice must UPDATE the user's existing onboarding-sourced row rather than
   * INSERT a second `user_brand_voices` row for the same user. Scoped to
   * `source = 'onboarding'` specifically (not "any voice") so it never
   * clobbers a manually-created or example/URL-sourced voice profile.
   */
  async upsertOnboardingBrandVoice(
    voice: Omit<BrandVoiceInsert, 'user_id' | 'source'>,
  ): Promise<ServiceResponse<BrandVoiceRow>> {
    return this.execute(async () => {
      const ownerId = await this.ensureAuthenticatedOwner();

      const sanitizedVoice = this.validateAndSanitizeBrandVoice({
        ...voice,
        source: 'onboarding',
      });

      const { data: existing, error: lookupError } = await this.client
        .from('user_brand_voices')
        .select('id')
        .eq('user_id', ownerId)
        .eq('source', 'onboarding')
        .order('created_at', { ascending: true })
        .limit(1)
        .maybeSingle();

      if (lookupError) throw lookupError;

      if (existing?.id) {
        const { data, error } = await this.client
          .from('user_brand_voices')
          .update(sanitizedVoice as BrandVoiceUpdate)
          .eq('id', existing.id)
          .eq('user_id', ownerId)
          .select()
          .maybeSingle();

        if (error) throw error;
        if (!data) throw ServiceError.notFound('Brand voice');

        this.clearCache(`brand-voice-${existing.id}`);
        this.clearCache(`brand-voices-${ownerId}`);

        return data;
      }

      const { data, error } = await this.client
        .from('user_brand_voices')
        .insert({
          ...sanitizedVoice,
          user_id: ownerId,
        } as BrandVoiceInsert)
        .select()
        .maybeSingle();

      if (error) throw error;
      if (!data) throw new Error('Failed to create brand voice');

      this.clearCache(`brand-voices-${ownerId}`);

      return data;
    }, 'Failed to save onboarding brand voice');
  }

  /**
   * Update an existing brand voice
   */
  async updateBrandVoice(
    id: string,
    updates: BrandVoiceUpdate,
  ): Promise<ServiceResponse<BrandVoiceRow>> {
    return this.execute(async () => {
      const ownerId = await this.ensureAuthenticatedOwner();

      // Remove undefined values
      const cleanedUpdates = Object.entries(updates).reduce((acc, [key, value]) => {
        if (value !== undefined) {
          acc[key as keyof BrandVoiceUpdate] = value;
        }
        return acc;
      }, {} as BrandVoiceUpdate);

      const { data, error } = await this.client
        .from('user_brand_voices')
        .update(cleanedUpdates as BrandVoiceUpdate)
        .eq('id', id)
        .eq('user_id', ownerId)
        .select()
        .maybeSingle();

      if (error) {
        throw error;
      }

      if (!data) {
        throw ServiceError.notFound('Brand voice');
      }

      // Clear relevant caches
      this.clearCache(`brand-voice-${id}`);
      this.clearCache(`brand-voices-${ownerId}`);

      return data;
    }, 'Failed to update brand voice');
  }

  /**
   * Update voice settings for a brand voice
   */
  async updateVoiceSettings(
    id: string,
    voiceSettings: { tone: number; engagement: number; creativity: number; length: number },
  ): Promise<ServiceResponse<BrandVoiceRow>> {
    return this.updateBrandVoice(id, { voice_attributes: voiceSettings } as any);
  }

  /**
   * Delete a brand voice
   */
  async deleteBrandVoice(id: string): Promise<ServiceResponse<void>> {
    return this.execute(async () => {
      const ownerId = await this.ensureAuthenticatedOwner();

      const { data, error } = await this.client
        .from('user_brand_voices')
        .delete()
        .eq('id', id)
        .eq('user_id', ownerId)
        .select()
        .maybeSingle();

      if (error) {
        throw error;
      }

      if (!data) {
        throw ServiceError.notFound('Brand voice');
      }

      // Clear relevant caches
      this.clearCache(`brand-voice-${id}`);
      this.clearCache(`brand-voices-${ownerId}`);
    }, 'Failed to delete brand voice');
  }

  /**
   * Get brand voices by profile name
   */
  async getBrandVoicesByName(profileName: string): Promise<ServiceResponse<BrandVoiceRow[]>> {
    return this.execute(async () => {
      const ownerId = await this.ensureAuthenticatedOwner();

      const { data, error } = await this.client
        .from('user_brand_voices')
        .select(
          'id, user_id, profile_name, bio, tone, target_audience, keywords, unique_selling_proposition, avoid_phrases, created_at, updated_at, is_active',
        )
        .eq('user_id', ownerId)
        .ilike('profile_name', `%${profileName}%`);

      if (error) throw error;

      return data || [];
    });
  }

  /**
   * Check if a profile name already exists for the user
   */
  async isProfileNameTaken(
    profileName: string,
    excludeId?: string,
  ): Promise<ServiceResponse<boolean>> {
    return this.execute(async () => {
      const ownerId = await this.ensureAuthenticatedOwner();

      let query = this.client
        .from('user_brand_voices')
        .select('id')
        .eq('user_id', ownerId)
        .eq('profile_name', profileName);

      if (excludeId) {
        query = query.neq('id', excludeId);
      }

      const { data, error } = await query;

      if (error) throw error;

      return (data || []).length > 0;
    });
  }

  /**
   * Get the default brand voice for the user
   */
  async getDefaultBrandVoice(): Promise<ServiceResponse<BrandVoiceRow | null>> {
    return this.execute(async () => {
      const voicesResponse = await this.getBrandVoices({
        limit: 1,
        orderBy: 'created_at',
        orderDirection: 'asc',
      });

      if (voicesResponse.error) throw voicesResponse.error;

      return voicesResponse.data?.[0] || null;
    });
  }

  /**
   * Bulk update multiple brand voices
   */
  async bulkUpdateBrandVoices(
    updates: Array<{ id: string; updates: BrandVoiceUpdate }>,
  ): Promise<ServiceResponse<BrandVoiceRow[]>> {
    return this.execute(async () => {
      const ownerId = await this.ensureAuthenticatedOwner();
      const results: BrandVoiceRow[] = [];

      // Use Promise.all for parallel updates
      await Promise.all(
        updates.map(async ({ id, updates }) => {
          const response = await this.updateBrandVoice(id, updates);
          if (response.data) {
            results.push(response.data);
          }
        }),
      );

      // Clear user's cache after bulk update
      this.clearCache(`brand-voices-${ownerId}`);

      return results;
    }, 'Failed to bulk update brand voices');
  }

  async getBrandAspirations(brandVoiceId: string): Promise<ServiceResponse<BrandAspirationsData>> {
    return this.execute(async () => {
      const ownerId = await this.ensureAuthenticatedOwner();
      const { data, error } = await this.client
        .from('user_brand_voices')
        .select('brand_aspirations')
        .eq('id', brandVoiceId)
        .eq('user_id', ownerId)
        .maybeSingle();

      if (error) throw error;

      const raw = (data?.brand_aspirations as Record<string, unknown>) ?? {};
      return {
        inspiration_brands: Array.isArray(raw.inspiration_brands)
          ? (raw.inspiration_brands as string[])
          : [],
        aesthetic: typeof raw.aesthetic === 'string' ? raw.aesthetic : '',
        voice_casual: typeof raw.voice_casual === 'number' ? raw.voice_casual : 50,
        voice_boldness: typeof raw.voice_boldness === 'number' ? raw.voice_boldness : 50,
      };
    });
  }

  async saveBrandAspirations(
    brandVoiceId: string,
    aspirations: BrandAspirationsData,
  ): Promise<ServiceResponse<BrandAspirationsData>> {
    return this.execute(async () => {
      const ownerId = await this.ensureAuthenticatedOwner();

      const cleaned: BrandAspirationsData = {
        inspiration_brands: aspirations.inspiration_brands.map((b) => b.trim()).filter(Boolean),
        aesthetic: aspirations.aesthetic.trim(),
        voice_casual: Math.max(0, Math.min(100, aspirations.voice_casual)),
        voice_boldness: Math.max(0, Math.min(100, aspirations.voice_boldness)),
      };

      const { error } = await this.client
        .from('user_brand_voices')
        .update({ brand_aspirations: cleaned as unknown as Json })
        .eq('id', brandVoiceId)
        .eq('user_id', ownerId);

      if (error) throw error;

      this.clearCache(`brand-voice-${brandVoiceId}`);
      this.clearCache(`brand-voices-${ownerId}`);

      return cleaned;
    }, 'Failed to save brand aspirations');
  }

  /**
   * Resolve the live default voice for a brand kit — the row the generation
   * pipeline reads and the Settings → Brand Voice editor writes.
   *
   * Resolution order (contract rules 1-2, 7):
   *   1. the kit's default live voice (brand_kit_id = kit, is_default, not archived)
   *   2. any live voice for the kit (fallback when no default is flagged yet)
   *   3. the account's single live voice with no kit — a transitional legacy row
   *      the editor stamps onto the kit on first save; the UI surfaces it with a
   *      "not yet linked" hint. Never enters implicit generation resolution.
   */
  async getKitDefaultVoice(kitId: string): Promise<ServiceResponse<BrandVoiceRow | null>> {
    return this.execute(async () => {
      const ownerId = await this.ensureAuthenticatedOwner();

      const { data: kitDefault, error: kitDefaultError } = await this.client
        .from('user_brand_voices')
        .select('*')
        .eq('user_id', ownerId)
        .eq('brand_kit_id', kitId)
        .eq('is_default', true)
        .is('archived_at', null)
        .order('updated_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (kitDefaultError) throw kitDefaultError;
      if (kitDefault) return kitDefault as BrandVoiceRow;

      const { data: anyKitVoice, error: anyKitVoiceError } = await this.client
        .from('user_brand_voices')
        .select('*')
        .eq('user_id', ownerId)
        .eq('brand_kit_id', kitId)
        .is('archived_at', null)
        .order('is_default', { ascending: false })
        .order('updated_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (anyKitVoiceError) throw anyKitVoiceError;
      if (anyKitVoice) return anyKitVoice as BrandVoiceRow;

      const { data: unlinkedVoice, error: unlinkedVoiceError } = await this.client
        .from('user_brand_voices')
        .select('*')
        .eq('user_id', ownerId)
        .is('brand_kit_id', null)
        .is('archived_at', null)
        .order('is_default', { ascending: false })
        .order('updated_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (unlinkedVoiceError) throw unlinkedVoiceError;
      return (unlinkedVoice as BrandVoiceRow) ?? null;
    }, 'Failed to load kit voice');
  }

  /**
   * Sanitize the editor's field patch: text fields are HTML-stripped + trimmed
   * (empty → null so a cleared field persists), array fields are trimmed +
   * emptied of blanks. Undefined fields are left out entirely (updateBrandVoice
   * drops them) so a partial diff never clobbers untouched columns.
   */
  private sanitizeVoiceFieldsPatch(patch: VoiceFieldsPatch): BrandVoiceUpdate {
    const stripText = (text: string | undefined): string | null | undefined => {
      if (text === undefined) return undefined;
      const cleaned = text.replace(/<[^>]*>/g, '').trim();
      return cleaned.length ? cleaned : null;
    };
    const cleanArray = (arr: string[] | undefined): string[] | undefined => {
      if (arr === undefined) return undefined;
      return arr
        .map((item) => (typeof item === 'string' ? item.replace(/<[^>]*>/g, '').trim() : ''))
        .filter(Boolean);
    };

    const update: BrandVoiceUpdate = {
      tone: stripText(patch.tone),
      industry: stripText(patch.industry),
      target_audience: stripText(patch.target_audience),
      business_overview: stripText(patch.business_overview),
      mission_statement: stripText(patch.mission_statement),
      unique_selling_proposition: stripText(patch.unique_selling_proposition),
      content_pillars: cleanArray(patch.content_pillars),
      keywords: cleanArray(patch.keywords),
      approved_phrases: cleanArray(patch.approved_phrases),
      avoid_phrases: cleanArray(patch.avoid_phrases),
      brand_kit_id: patch.brand_kit_id,
    };
    return update;
  }

  /**
   * Update the prompt-critical voice fields the Settings editor owns. Routes
   * through the shared sanitize + `updateBrandVoice` path (owner scoping +
   * cache invalidation), so it never bypasses RLS or leaves a stale cache.
   */
  async updateVoiceFields(
    id: string,
    patch: VoiceFieldsPatch,
  ): Promise<ServiceResponse<BrandVoiceRow>> {
    return this.updateBrandVoice(id, this.sanitizeVoiceFieldsPatch(patch));
  }

  /**
   * Clear all caches for brand voices
   */
  clearAllCaches(): void {
    // Clear all cache entries that start with 'brand-voice'
    // @ts-expect-error - accessing protected static property
    const cacheStore = BaseService.cacheStore;
    for (const key of cacheStore.keys()) {
      if (key.startsWith('brand-voice')) {
        this.clearCache(key);
      }
    }
  }
}

// Export singleton instance
export const brandVoiceService = BrandVoiceService.getInstance();
